import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { resolveApiBearerToken } from '@/app/admin/admin-auth';
import { handleInvalidTokenApiBody } from '@/app/utils/handle-invalid-token-response';

/** 从后端 JSON 体（如 { code: 1, message: "name 为必填", data: null }）取可读提示 */
function extractMessageFromResponseBody(data: unknown): string | null {
  let parsed: unknown = data;
  if (typeof data === 'string') {
    const t = data.trim();
    if (!t) return null;
    try {
      parsed = JSON.parse(t) as unknown;
    } catch {
      return t.length > 200 ? `${t.slice(0, 200)}…` : t;
    }
  }
  if (parsed == null || typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;
  const m = o.message;
  if (typeof m === 'string' && m.trim()) return m.trim();
  if (Array.isArray(m) && m.length > 0) return m.map(String).filter(Boolean).join('; ');
  return null;
}

function toFriendlyRequestError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const fromBody = extractMessageFromResponseBody(error.response?.data);
    if (fromBody) return new Error(fromBody);
    const status = error.response?.status;
    if (status === 401) return new Error('未登录或登录已过期');
    if (status === 403) return new Error('没有权限执行此操作');
    if (status === 404) return new Error('资源不存在');
    if (status != null && status >= 500) return new Error('服务器错误，请稍后重试');
    return new Error('请求失败，请稍后重试');
  }
  if (error instanceof Error) return error;
  return new Error(typeof error === 'string' ? error : '请求失败');
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  params?: Record<string, any>;
  body?: any;
}

export interface RequestConfig {
  baseURL?: string;
  timeoutMs?: number;
}

export type RequestInterceptor = (config: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>;
export type ResponseInterceptor = (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>;

export class Request {
  private instance: AxiosInstance;

  constructor(config: RequestConfig = {}) {
    this.instance = axios.create({
      baseURL: config.baseURL || '',
      timeout: config.timeoutMs || 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // 请求拦截器：这里可以统一加 token、traceId 等
    this.instance.interceptors.request.use(
      (config) => {
        // 示例：从 localStorage 里取 token
        const token = resolveApiBearerToken();
        if (token) {
          config.headers = config.headers || {};
          (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // 响应拦截器：HTTP 200 但 body.code≠0、或 HTTP 错误体含 Invalid token 时，后台页清 admin_token 并回登录
    this.instance.interceptors.response.use(
      (response) => {
        if (typeof window !== 'undefined') {
          handleInvalidTokenApiBody(response.data);
        }
        return response;
      },
      (error) => {
        if (axios.isAxiosError(error) && error.response?.data != null) {
          handleInvalidTokenApiBody(error.response.data);
        }
        return Promise.reject(toFriendlyRequestError(error));
      }
    );
  }

  async request<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', headers, params, body } = options;

    const axiosConfig: AxiosRequestConfig = {
      url,
      method,
      headers,
      params,
      data: body,
    };

    try {
      const resp = await this.instance.request<T>(axiosConfig);
      return resp.data;
    } catch (e) {
      throw toFriendlyRequestError(e);
    }
  }

  get<T = any>(url: string, params?: Record<string, any>, headers?: Record<string, string>) {
    return this.request<T>(url, { method: 'GET', params, headers });
  }

  post<T = any>(url: string, body?: any, headers?: Record<string, string>) {
    return this.request<T>(url, { method: 'POST', body, headers });
  }

  put<T = any>(url: string, body?: any, headers?: Record<string, string>) {
    return this.request<T>(url, { method: 'PUT', body, headers });
  }

  delete<T = any>(url: string, params?: Record<string, any>, headers?: Record<string, string>) {
    return this.request<T>(url, { method: 'DELETE', params, headers });
  }
}

/** 生产默认 API 根地址；本地可设 NEXT_PUBLIC_API_BASE_URL=http://127.0.0.1:3001/ */
function resolveDefaultApiBase(): string {
  return typeof process !== 'undefined' && process.env.NEXT_PUBLIC_API_BASE_URL
    ? process.env.NEXT_PUBLIC_API_BASE_URL
    : 'https://kehu221.fang47.com/';
}

const defaultApiBase = resolveDefaultApiBase();

/** 与 apiClient 同源，用于 file-proxy 等绝对 URL（无末尾 /） */
export function getApiPublicOrigin(): string {
  var origin = defaultApiBase.replace(/\/+$/, '');
  // 有些部署会把“前台域名(带 qt)”误填到 NEXT_PUBLIC_API_BASE_URL，
  // 导致文件下载请求打到 Next.js 的 `/api/file-proxy`（Next 路由）而不是后端 Express `/api/file-proxy`。
  // 这里做一个保守纠正：把 kehu221.qt.fang47.com / kehu221_qt.fang47.com 映射为 kehu221.fang47.com。
  origin = origin.replace(/^(.+?)\.qt\.fang47\.com$/i, '$1.fang47.com');
  origin = origin.replace(/^(.+?)_qt\.fang47\.com$/i, '$1.fang47.com');
  return origin;
}

export const apiClient = new Request({
  baseURL: defaultApiBase,
  timeoutMs: 15000,
}); 

